import { EventCalendar } from '@/app/core/models/calendar/eventCalendar';
import { CalendarGroupByDate } from '@/app/core/models/calendar/calendarGroupByDate';
import { SupportedLang } from '@/app/core/models/supportedLang';

export interface SportsEventContext {
	/** Langue utilisée pour résoudre `translations`. */
	lang: SupportedLang;
	/** Origine du site, sans slash final (ex. https://teampeps.fr). */
	siteUrl: string;
	/** Base des médias, avec slash final (ex. https://minio-s3.teampeps.fr/). */
	minioBaseUrl: string;
	/** URL canonique de la page qui porte le balisage (ex. https://teampeps.fr/matchs). */
	pageUrl: string;
}

const TEAM_PEPS_NAME = 'Team Peps';
const TEAM_PEPS_LOGO_PATH = '/assets/images/seo/logo-512.png';

/** Assemble base + clé média en tolérant les slashs en trop ou manquants. */
function mediaUrl(base: string, key: string | undefined | null): string | undefined {
	if (!key) return undefined;
	if (/^https?:\/\//i.test(key)) return key;
	return `${base.replace(/\/+$/, '')}/${key.replace(/^\/+/, '')}`;
}

/** Normalise la date de l'API en ISO 8601, exigé par schema.org. */
function toIsoDate(datetime: string | undefined): string | undefined {
	if (!datetime) return undefined;
	const parsed = new Date(datetime);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function teamPeps(ctx: SportsEventContext) {
	return {
		'@type': 'SportsTeam',
		name: TEAM_PEPS_NAME,
		url: ctx.siteUrl || undefined,
		logo: ctx.siteUrl ? `${ctx.siteUrl}${TEAM_PEPS_LOGO_PATH}` : undefined,
	};
}

function opponentTeam(event: EventCalendar, ctx: SportsEventContext) {
	const name = event.opponentData?.name ?? event.opponent;
	if (!name) return undefined;

	return {
		'@type': 'SportsTeam',
		name,
		logo: mediaUrl(ctx.minioBaseUrl, event.opponentImageKey ?? event.opponentData?.imageUrl),
	};
}

/** Retire les clés vides : schema.org préfère une propriété absente à une valeur vide. */
function prune(value: Record<string, unknown>): Record<string, unknown> {
	for (const key of Object.keys(value)) {
		const current = value[key];

		if (current === undefined || current === null || current === '') {
			delete value[key];
			continue;
		}

		if (Array.isArray(current)) {
			const cleaned = current
				.filter(item => item !== undefined && item !== null)
				.map(item => (item !== null && typeof item === 'object' ? prune(item as Record<string, unknown>) : item));
			if (cleaned.length === 0) delete value[key];
			else value[key] = cleaned;
			continue;
		}

		if (typeof current === 'object') {
			const cleaned = prune(current as Record<string, unknown>);
			// Un objet réduit à son seul `@type` n'apporte rien : on le retire.
			if (Object.keys(cleaned).length <= 1) delete value[key];
			else value[key] = cleaned;
		}
	}
	return value;
}

/**
 * Traduit une entrée de calendrier en `SportsEvent` schema.org.
 * Renvoie `null` si l'entrée n'a pas le minimum exigé par Google (un nom et une date).
 */
export function toSportsEvent(event: EventCalendar, ctx: SportsEventContext): Record<string, unknown> | null {
	const startDate = toIsoDate(event.datetime);
	if (!startDate) return null;

	const opponent = opponentTeam(event, ctx);
	const translatedName = event.translations?.[ctx.lang]?.name?.trim();
	const name = translatedName
		|| (opponent ? `${TEAM_PEPS_NAME} vs ${opponent.name}` : undefined);
	if (!name) return null;

	const competitionName = event.competitionData?.name ?? event.competitionName;
	const competitionImage = mediaUrl(ctx.minioBaseUrl, event.competitionImageKey ?? event.competitionData?.iconUrl);

	return prune({
		'@type': 'SportsEvent',
		name,
		startDate,
		url: ctx.pageUrl,
		eventStatus: 'https://schema.org/EventScheduled',
		eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
		// Un évènement diffusé en ligne : schema.org attend une VirtualLocation.
		// À défaut de flux annoncé, on rattache la page du calendrier.
		location: {
			'@type': 'VirtualLocation',
			url: event.streamUrl || ctx.pageUrl,
		},
		image: opponent?.logo ?? competitionImage,
		competitor: opponent ? [prune(teamPeps(ctx)), prune(opponent as Record<string, unknown>)] : undefined,
		organizer: competitionName ? { '@type': 'Organization', name: competitionName } : undefined,
		offers: event.ticketUrl
			? { '@type': 'Offer', url: event.ticketUrl, availability: 'https://schema.org/InStock' }
			: undefined,
	});
}

/**
 * Construit le graphe JSON-LD des prochains matchs.
 * Renvoie `null` si aucune entrée exploitable, pour ne pas publier un bloc vide.
 */
export function buildUpcomingEventsJsonLd(
	groups: CalendarGroupByDate[],
	ctx: SportsEventContext,
): Record<string, unknown> | null {
	const events = groups
		.flatMap(group => group.eventsCalendar ?? [])
		.map(event => toSportsEvent(event, ctx))
		.filter((event): event is Record<string, unknown> => event !== null);

	if (events.length === 0) return null;

	return {
		'@context': 'https://schema.org',
		'@graph': events,
	};
}
